import { useState } from 'react';
import { apiService } from '@/services/api.service';
import { PlayerAutocomplete } from '@/components/PlayerAutocomplete';
import { playerLookupName } from '@/utils/playerName';
import { GameGroupBadge } from '@/components/ui/GameGroupBadge';
import styles from './SanctionModal.module.scss';

type Kind = 'ban' | 'tempban' | 'mute' | 'warn';

const KINDS: { kind: Kind; label: string; type: number; temp: boolean }[] = [
  { kind: 'ban', label: 'Ban', type: 0, temp: false },
  { kind: 'tempban', label: 'Tempban', type: 0, temp: true },
  { kind: 'mute', label: 'Mute', type: 1, temp: true },
  { kind: 'warn', label: 'Warn', type: 2, temp: false },
];

interface Props {
  onClose: () => void;
  onCreated: () => void;
  /** Pré-remplit le champ joueur (ex. ouverture depuis un profil) : pseudo, gamertag ou uuid envoyé tel quel. */
  initialPlayer?: string;
  /** Verrouille le champ joueur (non modifiable, pas d'autocomplétion). */
  lockPlayer?: boolean;
  /** Texte affiché dans le champ verrouillé à la place de `initialPlayer` (ex. « pseudo (gamertag) »). */
  playerLabel?: string;
}

export function SanctionModal({ onClose, onCreated, initialPlayer = '', lockPlayer = false, playerLabel }: Props) {
  const [player, setPlayer] = useState(initialPlayer);
  // Suggestion choisie : tant que le champ garde son texte, la sanction vise son uuid, sans ambiguïté
  // entre le pseudo Recube d'un joueur et le gamertag d'un autre.
  const [picked, setPicked] = useState<{ value: string; uuid: string } | null>(null);
  const [kind, setKind] = useState<Kind>('ban');
  const [durationValue, setDurationValue] = useState(1);
  const [durationUnit, setDurationUnit] = useState<'m' | 'h' | 'd'>('d');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const meta = KINDS.find((k) => k.kind === kind) ?? KINDS[0]!;
  const unitMs = durationUnit === 'm' ? 60000 : durationUnit === 'h' ? 3600000 : 86400000;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!player.trim()) { setErr('Joueur requis'); return; }
    if (!reason.trim()) { setErr('Raison requise'); return; }
    if (meta.temp && durationValue <= 0) { setErr('Durée invalide'); return; }
    setSubmitting(true);
    const typed = player.trim();
    const res = await apiService.createSanction({
      player: picked && picked.value === typed ? picked.uuid : typed,
      type: meta.type,
      temporary: meta.temp,
      durationMs: meta.temp ? durationValue * unitMs : 0,
      reason: reason.trim(),
    });
    setSubmitting(false);
    if (res.error) setErr(res.error);
    else onCreated();
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 className={styles.modalTitle}>Sanctionner un joueur</h2>
        {/* Groupe de jeu qui reçoit la sanction (NationsGlory : Nova, Iris) ; rien pour Paladium. */}
        <GameGroupBadge block size="md" caption="Sanction posée sur" />
        <form onSubmit={submit} className={styles.modalForm}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Joueur (pseudo Recube, gamertag ou UUID)</span>
            {lockPlayer ? (
              <input className={styles.fieldInput} value={playerLabel ?? player} readOnly disabled />
            ) : (
              <PlayerAutocomplete
                value={player}
                onChange={setPlayer}
                onSelect={(s) => setPicked({ value: playerLookupName(s.name, s.displayName), uuid: s.uuid })}
                inputClassName={styles.fieldInput}
                autoFocus
                placeholder="Pseudo du joueur…"
              />
            )}
          </label>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Type</span>
            <div className={styles.kindRow}>
              {KINDS.map((k) => (
                <button
                  type="button"
                  key={k.kind}
                  className={`${styles.kindBtn} ${kind === k.kind ? styles.kindActive : ''}`}
                  onClick={() => setKind(k.kind)}
                >
                  {k.label}
                </button>
              ))}
            </div>
          </div>

          {meta.temp && (
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Durée</span>
              <div className={styles.durationRow}>
                <input
                  type="number"
                  min={1}
                  className={styles.fieldInput}
                  value={durationValue}
                  onChange={(e) => setDurationValue(Math.max(1, Number(e.target.value)))}
                />
                <select
                  className={styles.fieldInput}
                  value={durationUnit}
                  onChange={(e) => setDurationUnit(e.target.value as 'm' | 'h' | 'd')}
                >
                  <option value="m">minutes</option>
                  <option value="h">heures</option>
                  <option value="d">jours</option>
                </select>
              </div>
            </label>
          )}

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Raison</span>
            <textarea
              className={styles.fieldInput}
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Raison de la sanction…"
              autoFocus={lockPlayer}
            />
          </label>

          {err && <div className={styles.formError}>{err}</div>}

          <div className={styles.modalActions}>
            <button type="button" className={styles.btnGhost} onClick={onClose} disabled={submitting}>
              Annuler
            </button>
            <button type="submit" className={styles.btnPrimary} disabled={submitting}>
              {submitting ? 'Envoi…' : 'Sanctionner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
